'use client';

import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

const TOSSFACE_SX = {
  fontFamily: '"Tossface", "Apple Color Emoji", "Segoe UI Emoji", sans-serif',
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  flexShrink: 0,
} as const;

type MuiFontSize = 'small' | 'medium' | 'large' | 'inherit';

const MUI_FONT_SIZE_MAP: Record<MuiFontSize, string | number> = {
  small: '1.125rem',
  medium: '1.5rem',
  large: '2.25rem',
  inherit: 'inherit',
};

function resolveFontSize(fontSize: MuiFontSize | string | number | undefined): string | number {
  if (fontSize === undefined) return '1.25rem';
  if (typeof fontSize === 'number') return fontSize;
  if (fontSize in MUI_FONT_SIZE_MAP) return MUI_FONT_SIZE_MAP[fontSize as MuiFontSize];
  return fontSize;
}

export interface EmojiIconProps {
  sx?: SxProps<Theme>;
  fontSize?: MuiFontSize | string | number;
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  'aria-label'?: string;
  role?: string;
  /** MUI 아이콘 호환용 — 이모지는 색상 변경이 불가하므로 무시됨 */
  color?:
    | 'inherit'
    | 'action'
    | 'disabled'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | string;
}

/**
 * 토스페이스 이모지를 MUI 아이콘처럼 사용할 수 있는 래퍼 컴포넌트.
 * createEmojiIcon() 팩토리로 개별 아이콘을 생성해 사용.
 */
export function EmojiIcon({
  emoji,
  fontSize,
  sx,
  className,
  color: _color,
  ...rest
}: EmojiIconProps & { emoji: string }) {
  return (
    <Box
      component="span"
      aria-hidden
      className={className}
      sx={{
        ...TOSSFACE_SX,
        fontSize: resolveFontSize(fontSize),
        ...sx,
      }}
      {...rest}
    >
      {emoji}
    </Box>
  );
}

/**
 * 이모지 문자열로 MUI 아이콘 호환 컴포넌트를 생성하는 팩토리.
 *
 * @example
 * const HomeEmoji = createEmojiIcon('🏠');
 * <HomeEmoji fontSize="small" sx={{ color: ... }} />
 */
export function createEmojiIcon(emoji: string) {
  function Icon(props: EmojiIconProps) {
    return <EmojiIcon emoji={emoji} {...props} />;
  }
  Icon.displayName = `EmojiIcon(${emoji})`;
  return Icon;
}
